/**
 * Home.
 *
 * A tela responde "tem pelada e eu confirmei?" em menos de um segundo. Por
 * isso a próxima partida ocupa o topo com os dois botões de presença
 * embutidos — confirmar não deveria exigir navegar para outro lugar.
 */

import { Calendar, ChevronRight, MapPin, Plus, Star, Volleyball as Ball } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { GameCard } from '@/components/game-card';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ProgressBar,
  SectionTitle,
  Skeleton,
  capitalizeFirst,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useDashboard, useGroups, useRespondAttendance } from '@/lib/queries';
import { useAuth } from '@/store/auth';

export function DashboardPage() {
  const user = useAuth((state) => state.user);
  const dashboard = useDashboard();
  const groups = useGroups();

  const firstName = user?.name.split(' ')[0] ?? '';
  const nextGame = dashboard.data?.nextGame ?? null;
  const pendingRatings = dashboard.data?.pendingRatingsCount ?? 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Olá, {firstName}! 👋</h1>
          <p className="mt-0.5 text-sm text-ink-2">
            {groups.data?.length
              ? `Você participa de ${groups.data.length} pelada${groups.data.length > 1 ? 's' : ''}`
              : 'Bora organizar sua pelada?'}
          </p>
        </div>

        <Link to="/grupos">
          <Button icon={Plus}>Nova pelada</Button>
        </Link>
      </div>

      {/* Avaliação pendente vem antes de tudo: a janela fecha em 48 h. */}
      {pendingRatings > 0 && nextGame ? (
        <Card accent="rgb(220 104 3)">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-warn-soft">
                <Star className="h-4 w-4 text-warn" aria-hidden />
              </span>
              <div>
                <p className="font-semibold">Avalie os jogadores</p>
                <p className="text-[13px] text-ink-2">
                  {pendingRatings} avaliação{pendingRatings > 1 ? 'ões' : ''} pendente
                  {pendingRatings > 1 ? 's' : ''} — é anônimo.
                </p>
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {dashboard.isLoading ? (
        <Card>
          <div className="space-y-4">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-1.5 w-full" />
            <div className="flex gap-3">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 flex-1" />
            </div>
          </div>
        </Card>
      ) : dashboard.error ? (
        <ErrorState
          message={
            dashboard.error instanceof ApiError ? dashboard.error.message : 'Erro inesperado'
          }
          onRetry={() => void dashboard.refetch()}
        />
      ) : nextGame ? (
        <NextGameHero game={nextGame} />
      ) : groups.data?.length ? (
        <Card>
          <EmptyState
            icon={Calendar}
            title="Nenhuma pelada marcada"
            description="Assim que o administrador criar a próxima partida, ela aparece aqui."
          />
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={Ball}
            title="Crie sua primeira pelada"
            description="Monte o grupo, convide a galera e deixe o app cuidar dos times, da presença e do caixa."
            action={
              <Link to="/grupos">
                <Button icon={Plus}>Criar pelada</Button>
              </Link>
            }
          />
        </Card>
      )}

      {groups.data && groups.data.length > 0 ? (
        <section className="space-y-3">
          <SectionTitle
            action={
              <Link to="/grupos" className="text-[13px] font-semibold text-brand hover:underline">
                Ver todas
              </Link>
            }
          >
            Minhas peladas
          </SectionTitle>

          <div className="grid gap-3 sm:grid-cols-2">
            {groups.data.slice(0, 4).map((group) => (
              <Link key={group.id} to={`/grupos/${group.id}`}>
                <Card className="h-full transition-colors hover:border-line-strong">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate font-bold">{group.name}</p>
                        {group.myRole !== 'MEMBER' ? (
                          <Badge tone="brand">
                            {group.myRole === 'OWNER' ? 'Dono' : 'Admin'}
                          </Badge>
                        ) : null}
                      </div>

                      <p className="mt-1 text-[13px] text-ink-3">
                        {group.memberCount} participantes
                        {group.venueName ? ` · ${group.venueName}` : ''}
                      </p>
                    </div>

                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {dashboard.data?.recentGames.length ? (
        <section className="space-y-3">
          <SectionTitle>Últimas partidas</SectionTitle>
          <div className="grid gap-3 sm:grid-cols-2">
            {dashboard.data.recentGames.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} showGroup />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

/** Cartão de destaque da próxima pelada, com presença embutida. */
function NextGameHero({ game }: { game: NonNullable<ReturnType<typeof useDashboard>['data']>['nextGame'] }) {
  const navigate = useNavigate();
  const respond = useRespondAttendance(game!.id);

  if (!game) return null;

  const scheduledAt = new Date(game.scheduledAt);
  const confirmed = game.myAttendance === 'CONFIRMED';
  const declined = game.myAttendance === 'DECLINED';

  const when = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  }).format(scheduledAt);

  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(scheduledAt);

  return (
    <div className="rounded-card bg-brand p-6 text-on-brand lg:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide opacity-80">
            Próxima pelada
          </p>
          <h2 className="mt-1 text-2xl font-extrabold">{game.groupName}</h2>
        </div>
        <Ball className="h-9 w-9 opacity-30" aria-hidden />
      </div>

      <div className="mt-5 space-y-1.5 text-[15px]">
        <p className="flex items-center gap-2 font-semibold">
          <Calendar className="h-4 w-4" aria-hidden />
          {capitalizeFirst(when)} · {time}
        </p>
        {game.venueName ? (
          <p className="flex items-center gap-2 opacity-90">
            <MapPin className="h-4 w-4" aria-hidden />
            {game.venueName}
          </p>
        ) : null}
      </div>

      <div className="mt-5 space-y-1.5">
        <p className="tabular text-[13px] font-semibold">
          {game.confirmedCount} / {game.maxPlayers} confirmados
        </p>
        <div className="opacity-90">
          <ProgressBar value={game.confirmedCount} max={game.maxPlayers} tone="ok" />
        </div>
      </div>

      {/* Os dois botões que resolvem a maior parte do uso do produto. */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:flex">
        <button
          type="button"
          onClick={() => respond.mutate('CONFIRMED')}
          disabled={respond.isPending}
          className={`flex-1 whitespace-nowrap rounded-lg py-2.5 text-sm font-bold transition-colors disabled:opacity-60 ${
            confirmed
              ? 'bg-on-brand text-brand'
              : 'bg-on-brand/15 text-on-brand hover:bg-on-brand/25'
          }`}
        >
          {confirmed ? '✓ Estou dentro' : 'Estou dentro'}
        </button>

        <button
          type="button"
          onClick={() => respond.mutate('DECLINED')}
          disabled={respond.isPending}
          className={`flex-1 whitespace-nowrap rounded-lg py-2.5 text-sm font-bold transition-colors disabled:opacity-60 ${
            declined
              ? 'bg-bad text-white'
              : 'bg-on-brand/15 text-on-brand hover:bg-on-brand/25'
          }`}
        >
          {declined ? '✓ Estou fora' : 'Estou fora'}
        </button>

        <button
          type="button"
          onClick={() => navigate(`/partidas/${game.id}`)}
          className="col-span-2 rounded-lg px-4 py-2 text-sm font-bold text-on-brand/85 underline-offset-2 hover:underline sm:py-2.5"
        >
          Ver detalhes
        </button>
      </div>

      {respond.data?.wentToWaitlist ? (
        <p className="mt-3 text-[13px] font-semibold">
          Pelada lotada — você entrou na lista de espera na posição{' '}
          {respond.data.waitlistPosition}. Avisamos se abrir vaga.
        </p>
      ) : null}
    </div>
  );
}
